import { signal } from '@angular/core';

import type {
  BillingAccount,
  BillingAddon,
  BillingBudget,
  BillingCatalogue,
  BillingDetails,
  BillingInvoice,
  BillingJob,
  BillingLimits,
  BillingMonth,
  BillingPlanChangePreview,
  BillingPurchase,
  BillingUsage,
} from 'src/app/pages/organizations/organization/settings/billing/data/billing.model';
import {
  DEMO_AUTO_STEPS,
  DEMO_CLONE_HOSTING,
  DEMO_GROUPS,
  DEMO_ORGS,
  DEMO_PACKS,
  DEMO_PLANS,
  DEMO_RATES,
  DEMO_TODAY,
  DemoJob,
} from './billing-demo-data';

/**
 * Plans & billing sample layer — answers the billing endpoints `backend/api` does not have yet, with the
 * mockup's dataset and its own arithmetic (spend order, automatic top-up, waiting jobs, proration), and keeps
 * this browser session's changes (purchases, limits, budgets, plan switches, removed add-ons, details).
 *
 * Its own switch, deliberately independent of `INVENTED_DATA_ENABLED` (Dani, 1 Oct: real data only): Ansar,
 * 6 Oct — customers do not use Plans & billing yet, so the page runs on marked sample data until David builds
 * the backend from `.claude-docs/plans-billing-API-CONTRACT.md`. Every answer it gives is recorded in
 * {@link billingSampleUsed}, which the page's "Sample data" mark reads.
 *
 * **To remove it:** delete the four `billing-demo-*` files and the word `billingDemoBackendInterceptor` from
 * `withInterceptors([...])` in `app.config.ts`.
 */
export const BILLING_DEMO_ENABLED = true;

export type BillingSampleOrg = 'lighthouse' | 'onehope';
export type BillingSampleSituation = 'normal' | 'low' | 'out';

/** What the sample layer answered, by endpoint family; the page's mark lists them. */
export type BillingSampleSource =
  | 'catalogue' | 'account' | 'usage' | 'purchases' | 'limits' | 'budgets' | 'plan' | 'addons' | 'invoices' | 'details' | 'card';

export const BILLING_SAMPLE_LABELS: Record<BillingSampleSource, string> = {
  catalogue: 'prices and plans',
  account: 'credit and plan',
  usage: 'usage',
  purchases: 'bought credit',
  limits: 'limits and alerts',
  budgets: 'project budgets',
  plan: 'plan changes',
  addons: 'add-ons',
  invoices: 'invoices',
  details: 'billing details',
  card: 'card',
};

const used = signal<ReadonlySet<BillingSampleSource>>(new Set());

/** Sources answered from samples so far; empty once the server answers everything. */
export const billingSampleUsed = used.asReadonly();

export function noteBillingSample(source: BillingSampleSource): void {
  if (used().has(source)) return;
  used.set(new Set([...used(), source]));
}

// ── Which example and situation (testing only: ?sampleOrg=onehope&sample=low on the billing page) ─

let sampleOrg: BillingSampleOrg = 'lighthouse';
let situation: BillingSampleSituation = 'normal';

/** Reads `sampleOrg` and `sample` from the page address; they stick until changed. */
export function pickSample(url: string): void {
  const q = new URLSearchParams(url.split('?')[1]?.split('#')[0] ?? '');
  const o = q.get('sampleOrg');
  const s = q.get('sample');
  if (o === 'lighthouse' || o === 'onehope') sampleOrg = o;
  if (s === 'normal' || s === 'low' || s === 'out') situation = s;
}

export function currentSample(): { org: BillingSampleOrg; situation: BillingSampleSituation } {
  return { org: sampleOrg, situation };
}

// ── Session changes, kept per example organization ────────────────────────────────────────────

interface Session {
  purchases: { paid: number; credit: number; date: string }[];
  limits: BillingLimits | null;
  budgets: Record<string, number | null>;
  /** An upgrade (applied today) or a downgrade (at renewal). */
  planChange: { to: string; when: string; from: string; payNow: number; creditNow: number } | null;
  planCancel: boolean;
  removedAddons: Record<string, true>;
  details: BillingDetails | null;
}

const sessions: Partial<Record<BillingSampleOrg, Session>> = {};

function S(): Session {
  return (sessions[sampleOrg] ??= { purchases: [], limits: null, budgets: {}, planChange: null, planCancel: false, removedAddons: {}, details: null });
}

/** Forgets every session change (specs). */
export function resetBillingSample(): void {
  for (const k of Object.keys(sessions) as BillingSampleOrg[]) delete sessions[k];
  sampleOrg = 'lighthouse';
  situation = 'normal';
  used.set(new Set());
}

// ── Arithmetic (the mockup's data.js) ─────────────────────────────────────────────────────────

const ROLL_DAYS = 60;
const r2 = (n: number) => Math.round((+n || 0) * 100) / 100;
const O = () => DEMO_ORGS[sampleOrg];
const today = () => DEMO_TODAY;

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
}

function addDays(iso: string, n: number): string {
  return new Date(Date.parse(iso + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
}

function money(n: number, cents = true): string {
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
}

function person(id: string) {
  return O().people.find((p: any) => p.id === id) ?? { id, name: 'Unknown', initials: '?', role: 'Member' };
}

function project(id: string) {
  return O().projects.find((p: any) => p.id === id) ?? { id, name: 'No project', budget: null };
}

function admin() {
  return O().people.find((p: any) => p.role === 'Admin') ?? O().people[0];
}

function rawPlan(id: string) {
  return DEMO_PLANS.find(p => p.id === id);
}

/** The plan in force today: an upgrade made this session applies at once. */
function currentPlanId(): string {
  const pc = S().planChange;
  return pc && pc.when <= today() ? pc.to : O().subscription.plan;
}

/** A plan as this organization sees it: its agreement fills in the fee and credit. */
function plan(id = currentPlanId()) {
  const p = rawPlan(id);
  const a = O().agreement;
  if (p && a && a.plan === p.id) return { ...p, fee: a.fee, credit: a.credit, bonus: a.bonus, packBonus: a.packBonus };
  return p;
}

function planCredit(p: any): number | null {
  const q = p ? plan(p.id) : plan();
  return q && q.credit != null ? r2(q.credit * (1 + (q.bonus || 0) / 100)) : null;
}

/** This cycle's plan credit: the plan's, plus what an upgrade today added. */
function monthCredit(): number {
  const pc = S().planChange;
  const now = pc && pc.when <= today() ? pc : null;
  return r2((planCredit(rawPlan(O().subscription.plan)) ?? 0) + (now ? +now.creditNow : 0));
}

function offPct(): number {
  const d = O().discount;
  return d && d.until >= today() ? d.pct : 0;
}

function lineCost(l: { rate: string; qty: number }): number {
  return r2(DEMO_RATES[l.rate].price * l.qty);
}

function jobList(j: DemoJob): number {
  return r2(j.lines.reduce((t, l) => t + lineCost(l), 0));
}

function priceOf(list: number) {
  const p = offPct();
  const off = p ? r2(list * p / 100) : 0;
  return { list: r2(list), discount: off, pay: r2(list - off) };
}

function packBonus(p = plan()): number {
  const a = O().agreement;
  if (a && a.plan === p.id) return +a.packBonus || 0;
  return +p.packBonus || 0;
}

function packCredit(paid: number, p = plan()): number {
  return r2(paid * (1 + packBonus(p) / 100) / (p.buyAt || 1));
}

function limitsNow(): BillingLimits {
  return S().limits ?? seedLimits();
}

function inScenario(j: DemoJob): boolean {
  return !j.sc || j.sc.includes(situation);
}

interface Grant { id: string; date: string; paid: number; credit: number; usedBefore: number; expires: string; by: string | null; auto: boolean }

/** Bought credit: the dataset's purchases plus this session's. */
function allTopups(): Grant[] {
  const a = admin();
  const mine = S().purchases.map((x, i) => ({
    id: 'TU-NEW-' + (i + 1), date: x.date, paid: x.paid, credit: x.credit, usedBefore: 0, expires: addDays(x.date, ROLL_DAYS), by: a.id, auto: false,
  }));
  return [...O().topups.map((t: any) => ({ ...t, auto: false })), ...mine];
}

interface LedgerRow { job: DemoJob; cost: number; fromPlan: number; fromTopup: number; list: number; off: number }
interface Ledger {
  rows: LedgerRow[];
  waiting: { job: DemoJob; cost: number; short: number; list: number }[];
  planCredit: number;
  planLeft: number;
  topups: { id: string; date: string; left: number; expires: string }[];
  autoBuys: Grant[];
}

/**
 * Credit is spent in date order: plan credit first, then bought credit, oldest first. A job that needs more than
 * what is left does not run: it waits. Automatic top-up buys one step at a time, within the monthly cap.
 */
function ledger(): Ledger {
  const pc = monthCredit();
  let planLeft = pc;
  const start = O().subscription.cycleStart;
  const tops = allTopups().filter(t => t.expires >= start).map(t => ({ id: t.id, date: t.date, left: r2(t.credit - t.usedBefore), expires: t.expires }));
  const rows: LedgerRow[] = [];
  const waiting: Ledger['waiting'] = [];
  const autoBuys: Grant[] = [];
  const lim = limitsNow();
  const auto = lim.atZero === 'auto' || lim.autoTopup.on;
  const step = +lim.autoTopup.step || 50;
  const cap = lim.autoTopup.cap != null ? +lim.autoTopup.cap : (plan().fee ?? 0);
  const autoBuy = (date: string): boolean => {
    const spent = autoBuys.reduce((t, x) => t + x.paid, 0);
    // No top-up when switched off, when a pack would add nothing, or when it would pass the monthly cap.
    if (!auto || step <= 0 || r2(spent + step) > cap + 0.001) return false;
    const credit = packCredit(step);
    const id = 'AUTO-' + (autoBuys.length + 1);
    autoBuys.push({ id, date, paid: step, credit, usedBefore: 0, expires: addDays(date, ROLL_DAYS), by: null, auto: true });
    tops.push({ id, date, left: credit, expires: addDays(date, ROLL_DAYS) });
    return true;
  };
  const usable = (t: { date: string; expires: string }, d: string) => t.expires >= d && t.date <= d;
  const topsLeft = (d: string) => r2(tops.reduce((t, x) => t + (usable(x, d) ? x.left : 0), 0));
  const leftNow = (d: string) => r2(planLeft + topsLeft(d));
  const jobs = (O().jobs as DemoJob[]).filter(inScenario).slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  for (const j of jobs) {
    const pr = priceOf(jobList(j));
    const cost = pr.pay;
    const d = j.date;
    // Buy top-ups until the job fits or the cap stops it; the loop has a hard limit so bad limits can never spin forever.
    for (let buys = 0; buys < 100 && cost > leftNow(d) + 0.001; buys++) {
      if (!autoBuy(d)) break;
    }
    const topLeft = topsLeft(d);
    if (cost > r2(planLeft + topLeft) + 0.001) {
      waiting.push({ job: j, cost, short: r2(cost - planLeft - topLeft), list: pr.list });
      continue;
    }
    const fromPlan = Math.min(planLeft, cost);
    let rest = r2(cost - fromPlan);
    planLeft = r2(planLeft - fromPlan);
    let fromTop = 0;
    for (const t of tops) {
      if (rest <= 0 || !usable(t, d)) continue;
      const take = Math.min(t.left, rest);
      t.left = r2(t.left - take);
      rest = r2(rest - take);
      fromTop = r2(fromTop + take);
    }
    rows.push({ job: j, cost, fromPlan: r2(fromPlan), fromTopup: fromTop, list: pr.list, off: pr.discount });
    if (leftNow(d) < (+lim.autoTopup.below || 0)) autoBuy(d);
  }
  return { rows, waiting, planCredit: pc, planLeft: r2(planLeft), topups: tops, autoBuys };
}

// ── Answers ───────────────────────────────────────────────────────────────────────────────────

/** GET /billing/catalogue */
export function sampleCatalogue(): BillingCatalogue {
  return {
    version: 'Sheet v3, 16 Sep 2026',
    currency: 'USD',
    rates: Object.entries(DEMO_RATES).map(([key, r]) => ({ key, label: r.label, unit: r.unit, price: r.price, group: r.group as any })),
    groups: DEMO_GROUPS.map(g => ({ key: g.key as any, label: g.label })),
    plans: DEMO_PLANS.map(p => ({
      id: p.id, name: p.name, tag: p.tag, fee: p.fee, credit: p.credit, bonus: p.bonus, packBonus: p.packBonus,
      ...(p.buyAt ? { buyAt: p.buyAt } : {}),
      maxMembers: p.maxMembers ?? null, membersLabel: p.members, forWhom: p.for, features: p.features.slice(),
      ...(p.free ? { free: true } : {}), ...(p.noProjects ? { noProjects: true } : {}), ...(p.custom ? { custom: true } : {}),
    })),
    packs: DEMO_PACKS.slice(),
    autoTopupSteps: DEMO_AUTO_STEPS.slice(),
    approvalSteps: [10, 25, 50, 100],
    cloneHosting: DEMO_CLONE_HOSTING,
    cloneSetup: DEMO_RATES['AIC'].price,
    rollDays: ROLL_DAYS,
    basicDubbing: { rates: ['STT', 'STEM', 'TTSEL', 'SRT'], translationRate: 'TR', wordsPerMinute: 150 },
    wordsPerPage: 250,
  };
}

/** The plan change that started the current plan (the dataset's, or this session's upgrade). */
function lastChange(): BillingAccount['subscription']['lastChange'] {
  const pc = S().planChange;
  if (pc && pc.when <= today()) return { from: pc.from, on: pc.when, charged: pc.payNow, creditAdded: pc.creditNow };
  // The dataset's own history: Lighthouse moved from Pro to Team on 15 Jun (invoice WW-26-0615).
  const sub = O().subscription;
  const change = (O().invoices as any[]).find(inv => inv.date === sub.since && inv.lines.some((l: any[]) => /^Change from /.test(l[0])));
  if (!change) return null;
  const from = /^Change from (\w+) to /.exec(change.lines[0][0])?.[1] ?? '';
  const fromId = DEMO_PLANS.find(p => p.name === from)?.id ?? from.toLowerCase();
  return { from: fromId, on: sub.since, charged: r2(change.lines.reduce((t: number, l: any[]) => t + l[1], 0)), creditAdded: 0 };
}

/** GET /billing/{org}/account */
export function sampleAccount(orgId: string): BillingAccount {
  const o = O();
  const lg = ledger();
  const sub = o.subscription;
  const used = r2(lg.rows.reduce((t, r) => t + r.cost, 0));
  const usedAtList = r2(lg.rows.reduce((t, r) => t + r.list, 0));
  const boughtLeft = r2(lg.topups.reduce((t, x) => t + (x.expires >= today() ? x.left : 0), 0));
  const pc = S().planChange;
  const card = o.billing.card;
  return {
    asOf: today(),
    organization: { id: orgId, name: o.org.name, country: o.org.country },
    team: o.people.map((p: any) => ({ id: p.id, name: p.name, initials: p.initials, role: p.role === 'Admin' ? 'admin' : 'member' })),
    subscription: {
      planId: currentPlanId(),
      since: pc && pc.when <= today() ? pc.when : sub.since,
      lastChange: lastChange(),
      cycleStart: sub.cycleStart,
      cycleEnd: sub.cycleEnd,
      renews: sub.renews,
      endsOn: S().planCancel ? sub.cycleEnd : null,
      pendingChange: pc && pc.when > today() ? { planId: pc.to, effective: pc.when } : null,
    },
    agreement: o.agreement ? { name: o.agreement.name, planId: o.agreement.plan, fee: o.agreement.fee, credit: o.agreement.credit, bonus: o.agreement.bonus, packBonus: o.agreement.packBonus, from: o.agreement.from, until: o.agreement.until } : null,
    discount: o.discount ? { ...o.discount } : null,
    credit: {
      planCredit: lg.planCredit, planUsed: r2(lg.planCredit - lg.planLeft), planLeft: lg.planLeft,
      boughtLeft, available: r2(lg.planLeft + boughtLeft), used, usedAtList,
    },
    waiting: lg.waiting.map(w => ({
      jobId: w.job.id, date: w.job.date, title: w.job.title, detail: w.job.detail, projectId: w.job.project,
      projectName: project(w.job.project).name, cost: w.cost, list: w.list, short: w.short,
      person: { id: person(w.job.person).id, name: person(w.job.person).name, initials: person(w.job.person).initials },
      lines: w.job.lines.map(l => ({ rate: l.rate, qty: l.qty, ...(l.voice ? { voice: l.voice } : {}), ...(l.of ? { of: l.of } : {}), list: lineCost(l) })),
      redo: !!w.job.redo,
      approval: w.job.approval ? { by: { id: w.job.approval.by, name: person(w.job.approval.by).name }, date: w.job.approval.date, note: w.job.approval.note } : null,
    })),
    autoTopup: { bought: lg.autoBuys.length, spent: r2(lg.autoBuys.reduce((t, x) => t + x.paid, 0)) },
    card: card.onFile
      ? { onFile: true, brand: null, last4: null, expMonth: null, expYear: null }
      : { onFile: false, brand: card.brand, last4: '4242', expMonth: Number(card.exp.slice(5, 7)), expYear: Number(card.exp.slice(0, 4)) },
    storage: { ...o.storage },
  };
}

/** GET /billing/{org}/usage */
export function sampleUsage(): BillingUsage {
  const o = O();
  const lg = ledger();
  const jobs: BillingJob[] = lg.rows.map(r => {
    const j = r.job;
    const p = person(j.person);
    return {
      id: j.id, date: j.date, title: j.title, detail: j.detail, projectId: j.project, projectName: project(j.project).name,
      person: { id: p.id, name: p.name, initials: p.initials }, redo: !!j.redo,
      lines: j.lines.map(l => ({ rate: l.rate, qty: l.qty, ...(l.voice ? { voice: l.voice } : {}), ...(l.of ? { of: l.of } : {}), list: lineCost(l) })),
      list: r.list, discount: r.off, charged: r.cost, fromPlan: r.fromPlan, fromBought: r.fromTopup,
      approval: j.approval ? { by: { id: j.approval.by, name: person(j.approval.by).name }, date: j.approval.date, note: j.approval.note } : null,
    };
  }).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : (a.id < b.id ? 1 : -1)));
  const months: BillingMonth[] = o.history.map((h: any) => ({
    key: h.key, label: h.label, used: h.used, ...(h.list != null ? { list: h.list } : {}), planName: h.plan, credit: h.credit,
    fromBought: h.fromTopup, ranOutOn: h.ranOut ?? null,
  }));
  return { cycleStart: o.subscription.cycleStart, cycleEnd: o.subscription.cycleEnd, jobs, months };
}

/** GET /billing/{org}/credit/purchases — every purchase still usable, oldest first. */
export function samplePurchases(): BillingPurchase[] {
  const lg = ledger();
  const left = new Map(lg.topups.map(t => [t.id, t.left]));
  const all = [...allTopups(), ...lg.autoBuys];
  return all
    .filter(t => t.expires >= today())
    .map(t => {
      const l = left.has(t.id) ? left.get(t.id)! : r2(t.credit - t.usedBefore);
      const by = t.by ? person(t.by) : null;
      return {
        id: t.id, date: t.date, paid: t.paid, credit: t.credit, used: r2(t.credit - l), left: l, until: t.expires, auto: t.auto,
        boughtBy: by ? { id: by.id, name: by.name, initials: by.initials } : null,
      };
    })
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** POST /billing/{org}/credit/purchases {amount} */
export function sampleBuy(amountPaid: number): BillingPurchase {
  const credit = packCredit(amountPaid);
  S().purchases.push({ paid: amountPaid, credit, date: today() });
  return samplePurchases().filter(p => !p.auto).slice(-1)[0];
}

function seedLimits(): BillingLimits {
  const d = O().limits;
  return {
    atZero: d.atZero,
    autoTopup: { on: !!d.autoTopup.on, below: d.autoTopup.below, step: d.autoTopup.step, cap: d.autoTopup.cap ?? null },
    approvalOver: d.approvalOver,
    approvalSteps: (d.approvalSteps ?? [10, 25, 50, 100]).slice(),
    whoCanBuy: d.whoCanBuy === 'Admins' ? 'admins' : 'everyone',
    alerts: {
      credit80: !!d.alerts.find((a: any) => a.at === 80)?.on,
      credit100: !!d.alerts.find((a: any) => a.at === 100)?.on,
      budgets: true,
      weekly: false,
    },
  };
}

/** GET /billing/{org}/limits */
export function sampleLimits(): BillingLimits {
  return structuredClone(limitsNow());
}

/** PUT /billing/{org}/limits */
export function sampleSaveLimits(body: Partial<BillingLimits>): BillingLimits {
  const prev = limitsNow();
  S().limits = { ...prev, ...body, autoTopup: { ...prev.autoTopup, ...(body.autoTopup ?? {}) }, alerts: { ...prev.alerts, ...(body.alerts ?? {}) } };
  return sampleLimits();
}

/** GET /billing/{org}/budgets — every active project, with its budget or null. */
export function sampleBudgets(): BillingBudget[] {
  const over = S().budgets;
  return O().projects.map((p: any) => ({ projectId: p.id, projectName: p.name, amount: p.id in over ? over[p.id] : p.budget }));
}

/** PUT /billing/{org}/budgets/{project} {amount} · DELETE (amount null). */
export function sampleSetBudget(projectId: string, amountValue: number | null): BillingBudget | null {
  if (!O().projects.some((p: any) => p.id === projectId)) return null;
  S().budgets[projectId] = amountValue;
  return sampleBudgets().find(b => b.projectId === projectId) ?? null;
}

/** What switching to a priced plan does: an upgrade charges the days left now; a downgrade waits for renewal. */
export function samplePlanChange(to: string): BillingPlanChangePreview | null {
  const target = plan(to);
  const from = plan();
  if (!target || target.fee == null || target.id === from.id) return null;
  const sub = O().subscription;
  const days = daysBetween(today(), sub.cycleEnd) + 1;
  const len = daysBetween(sub.cycleStart, sub.cycleEnd) + 1;
  if (target.fee > (from.fee ?? 0)) {
    return {
      planId: to, when: 'now', effective: today(), daysLeft: days,
      payNow: r2((target.fee - (from.fee ?? 0)) * days / len),
      creditNow: r2(((planCredit(target) ?? 0) - (planCredit(from) ?? 0)) * days / len),
    };
  }
  return { planId: to, when: 'renewal', effective: sub.renews, daysLeft: days, payNow: 0, creditNow: 0 };
}

/** PUT /billing/{org}/plan {planId} */
export function sampleSwitchPlan(to: string): BillingAccount['subscription'] | null {
  const f = samplePlanChange(to);
  if (!f) return null;
  S().planChange = { to, when: f.effective, from: currentPlanId(), payNow: f.payNow, creditNow: f.creditNow };
  S().planCancel = false;
  return sampleAccount('').subscription;
}

/** DELETE /billing/{org}/plan/change — keep the current plan (undo a downgrade waiting for renewal). */
export function sampleKeepPlan(): BillingAccount['subscription'] {
  const pc = S().planChange;
  if (pc && pc.when > today()) S().planChange = null;
  return sampleAccount('').subscription;
}

/** POST /billing/{org}/plan/cancel · DELETE /billing/{org}/plan/cancel */
export function sampleCancelPlan(cancel: boolean): BillingAccount['subscription'] {
  S().planCancel = cancel;
  if (cancel && S().planChange && S().planChange!.when > today()) S().planChange = null;
  return sampleAccount('').subscription;
}

function cloneMonthly(): number {
  return r2(DEMO_CLONE_HOSTING * (1 - offPct() / 100));
}

/** GET /billing/{org}/addons — a removed one stays listed with the day its hosting stops (the renewal). */
export function sampleAddons(): BillingAddon[] {
  return O().addons.map((a: any) => ({
    id: a.id, kind: a.kind, name: a.name, since: a.since, monthly: a.monthly, list: a.list ?? a.monthly, usedIn: a.usedIn,
    endsOn: S().removedAddons[a.id] ? O().subscription.renews : null,
  }));
}

/** DELETE /billing/{org}/addons/{id} — hosting stops at renewal. POST …/keep (`keep`) takes that back. */
export function sampleRemoveAddon(id: string, keep = false): BillingAddon | null {
  if (!O().addons.some((a: any) => a.id === id)) return null;
  if (keep) delete S().removedAddons[id];
  else S().removedAddons[id] = true;
  return sampleAddons().find(a => a.id === id) ?? null;
}

function hostingLine(n: number): any[] {
  const line: any[] = ['Voice clone hosting, ' + n + (n === 1 ? ' clone' : ' clones'), r2(n * cloneMonthly())];
  if (offPct()) line.push({ note: money(DEMO_CLONE_HOSTING, false) + ' − ' + offPct() + ' % = ' + money(cloneMonthly(), cloneMonthly() % 1 !== 0) + ' a clone' });
  return line;
}

/** GET /billing/{org}/invoices — newest first; the upcoming one reflects this session's changes. */
export function sampleInvoices(): BillingInvoice[] {
  const st = S();
  const pc = st.planChange;
  const adds = O().addons;
  const gone = adds.filter((a: any) => st.removedAddons[a.id]).length;
  const kept = adds.length - gone;
  const list: any[] = [];
  for (const inv of O().invoices as any[]) {
    const x: any = { ...inv, lines: inv.lines.map((l: any[]) => l.slice()) };
    if (x.state === 'upcoming') {
      if (st.planCancel) continue;                                      // no plan, nothing charged on the 1st
      const to = pc && plan(pc.to);
      if (to) {
        x.lines = x.lines.map((l: any[]) => {
          if (!/\bplan, /i.test(l[0])) return l;
          (x.was ??= []).push(l[0] + ' ' + money(l[1]));
          return [to.name + ' plan, ' + l[0].split(', ').slice(1).join(', '), to.fee];
        });
      }
      if (gone) {
        x.lines = x.lines.filter((l: any[]) => !/^Voice clone hosting/i.test(l[0]) || kept > 0).map((l: any[]) => {
          if (!/^Voice clone hosting/i.test(l[0])) return l;
          (x.was ??= []).push(l[0] + ' ' + money(l[1]));
          return hostingLine(kept);
        });
      }
    }
    list.push(x);
  }
  // One-off invoices made in this session: the upgrade's days left, packs bought by hand and by automatic top-up.
  const extra: any[] = [];
  const packNote = offPct() ? { note: 'Priced by your agreement · not discounted again' } : null;
  const packLine = (label: string, t: { paid: number; credit: number }) => {
    const l: any[] = [label + money(t.paid, t.paid % 1 !== 0) + ' (adds ' + money(t.credit) + ' of credit)', t.paid];
    if (packNote) l.push(packNote);
    return l;
  };
  if (pc && pc.when <= today() && pc.payNow) {
    const sub = O().subscription;
    const span = +pc.when.slice(8, 10) + '–' + +sub.cycleEnd.slice(8, 10) + ' ' + new Date(sub.cycleEnd + 'T00:00:00Z').toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    extra.push({ date: pc.when, lines: [['Change from ' + (plan(pc.from)?.name ?? 'your plan') + ' to ' + (plan(pc.to)?.name ?? 'the new plan') + ', ' + span, pc.payNow]] });
  }
  st.purchases.forEach(t => extra.push({ date: t.date, lines: [packLine('Credit pack ', t)] }));
  ledger().autoBuys.forEach(t => extra.push({ date: t.date, auto: true, lines: [packLine('Automatic top-up: credit pack ', t)] }));
  const perDay: Record<string, number> = {};
  for (const x of extra) {
    const base = 'WW-' + x.date.slice(2, 4) + '-' + x.date.slice(5, 7) + x.date.slice(8, 10);
    const k = (perDay[base] = (perDay[base] ?? 0) + 1);
    list.push({ id: base + (k > 1 ? '-' + k : ''), date: x.date, due: x.date, state: 'paid', paidOn: x.date, auto: !!x.auto, lines: x.lines });
  }
  return list
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : (a.id < b.id ? 1 : -1)))
    .map(x => ({
      id: x.id, date: x.date, due: x.due, status: x.state, paidOn: x.paidOn ?? null,
      lines: x.lines.map((l: any[]) => ({ label: l[0], amount: l[1], ...(l[2]?.note ? { note: l[2].note } : {}) })),
      total: r2(x.lines.reduce((t: number, l: any[]) => t + l[1], 0)),
      ...(x.auto ? { auto: true } : {}), ...(x.was ? { was: x.was } : {}), pdfUrl: null,
    }));
}

/** GET /organization/{org}/billing — the Home's billing part, plus what invoices print. */
export function sampleDetails(): BillingDetails {
  if (S().details) return { ...S().details! };
  const b = O().billing;
  if (b.onFile) {
    return { legalName: b.legal, country: b.country, address: '', city: '', zip: '', region: '', taxId: '', noTaxId: false, invoiceEmail: '', contact: b.contact, onFile: true };
  }
  // "1200 Harbor Way, Portland, OR 97209" → street, city, region, zip.
  const m = /^(.*?), (.*?), ([A-Z]{2}) (\d{5})$/.exec(b.address);
  return {
    legalName: b.legal, country: b.country, address: m ? m[1] : b.address, city: m ? m[2] : '', region: m ? m[3] : '', zip: m ? m[4] : '',
    taxId: b.taxId, noTaxId: false, invoiceEmail: b.email, contact: b.contact,
  };
}

/** PUT /organization/{org}/billing */
export function sampleSaveDetails(body: Partial<BillingDetails>): BillingDetails {
  S().details = { ...sampleDetails(), ...body };
  return sampleDetails();
}

/** For the specs: the mockup's own self-check figures. */
export const _forSpecs = { ledger, planCredit, plan, packCredit, offPct, setSample: (o: BillingSampleOrg, s: BillingSampleSituation) => { sampleOrg = o; situation = s; } };

/** The jobs in the dataset (specs). */
export function sampleJobs(): DemoJob[] {
  return O().jobs;
}

